import React, { useMemo } from 'react';
import { BONES } from 'js/design_system/skeleton/types';
import 'app/assets/stylesheets/design_system/_skeleton.scss';

type Props = {
  /** The skeleton loader type. See {@link Skeleton} for more details. */
  type: string;
};

const SkeletonBone: React.FC<Props> = ({ type }) => {
  const bones = useMemo(
    () =>
      type
        .split(' ')
        .map((bone) => {
          const [type, count, _] = (bone + '@1').split('@');
          return new Array<string>(parseInt(count) || 0).fill(type);
        })
        .flat(),
    [type],
  );

  return (
    <>
      {bones.map((bone, index) => {
        const className = `skeleton__bone skeleton__${bone}`;
        return (
          <div key={index} className={className}>
            {bone in BONES && <SkeletonBone type={BONES[bone]} />}
          </div>
        );
      })}
    </>
  );
};

/**
 * A skeleton loader component. Skeleton loaders provide placeholder indicators
 * while waiting for asynchronous content to return. They're helpful in place of a
 * loading indicator (spinner) because they provide the illusion of progress and
 * lessen cognitive overhead when processing information on a page.
 *
 * Interfaces should use skeletons under these conditions:
 * * The average load time of an API call is > ~0.5s
 * * The user interface is central to the page
 * * The type of content is relatively consistent
 *
 * The **type** of skeleton is specified by any string–it's up to the developer
 * to define what the type looks like visually. A type can consist of multiple sub-types (parent type),
 * or it can be a "leaf" type. A parent type should be of the form `<type>( <type>)*`, i.e. a
 * space-separated list of sub-types. A single type should be of the form `<name>(@<count>)?`, i.e.
 * the type name followed by an optional count. Here are two example types:
 * * `news: 'user-info news-content news-actions'`
 * * `'news-actions': 'button@3'`
 *
 * Internally, a type name creates a `div` with the class name `skeleton__<name>`. You can define
 * styles for your type by defining rules for this class name in _skeleton.scss.
 */
export const Skeleton: React.FC<Props> = ({ type }) => {
  return (
    <div
      className="skeleton"
      role="alert"
      aria-busy={true}
      aria-live="polite"
      aria-label="Loading..."
    >
      <SkeletonBone type={type} />
    </div>
  );
};
